/**
 * The customer accounts list and its add/edit form (T651 M2 scaffolding, filled
 * by M3).
 *
 * ⭐ WHAT THIS SCREEN IS FOR, IN ONE LINE: adding customer number three is DATA,
 * not a deploy. Before it, the accounts were a hard-coded client constant, so a
 * new home meant a code change, a release and a developer.
 *
 * ⛔ ROUTING IS THE PARENT'S AND STAYS THERE. Admin.tsx owns the view union and
 * the loaders; AccountBook.tsx owns the tabs, the truncation notice and this
 * file's prop contract. Nothing here reopens either - the create form and the
 * `onChanged` invalidation were both landed up front for exactly that reason.
 *
 * ⛔ DEACTIVATE, NEVER DELETE (R38). There is no delete control and no delete
 * route: history attached to an account survives, bookings.account_id has no
 * cascade, and the account NAME is the meaning of every row that quotes it. What
 * `active = false` MEANS is decided once, at core/data/accounts-repo.ts's
 * setAccountActiveOn, and this file is its READER: a closed account leaves this
 * list's default view. That reader is the point - this schema already carries
 * three flags nothing reads (drivers.active, vehicles.active,
 * portal.homes.active), and a fourth would be decoration.
 *
 * ⛔ NO ROW CONTROL CARRIES THE ACCOUNT'S NAME IN ITS ACCESSIBLE NAME, and that
 * is two rules at once, not a style choice:
 *  1. PRIVACY. The feedback widget's click recorder reads a control's
 *     aria-label / text into a stored trail, and its own note says the
 *     sanitiser "catches emails and 7+ digit runs, never names". The shipped
 *     precedent next door interpolates the operator's LOGIN name (a constrained
 *     charset), never their display name, for the same reason.
 *  2. TEST STABILITY. An account name is arbitrary operator input. A row button
 *     called `Edit <name>` would let somebody create an account called
 *     "Continue" and change what a Playwright accessible-name matcher resolves
 *     to on a completely different screen.
 * The row's identity is carried by the list context instead.
 *
 * ⚠️ S28 (a) - THE CLICK CHANNEL - IS MARKED HERE, BUT ONE MARKER CARRIES IT.
 * Every control below is `data-fb-opaque`, read by feedback/clicks.ts, and the
 * blanket is deliberate - a uniform rule survives edits better than a rule
 * applied to the one element somebody reasoned about. But do not read the count
 * as the strength: on this screen exactly ONE of them changes what the recorder
 * stores, the "Bills to" <select> below, because a <select> is interactive and
 * clicks.ts takes `el.textContent` for a control - which on a <select> is every
 * option CONCATENATED, i.e. payer organisation names. (Its one load-bearing
 * sibling, an account picker on the nursing capture screen, went at board 2814
 * M6.)
 * The rest are inert TODAY and are future-proofing, not guards: the row buttons
 * and the two form buttons are labelled with static copy ("Edit", "Add
 * account"), and an <input>'s textContent is empty, so a text field falls to the
 * id branch and describeTarget answers `input#acct-name` marked or unmarked.
 * ⚠️ Note where the PII actually sits in a row - in the <span class="op-name">
 * SIBLING of the buttons, not inside them. A <span> is not in
 * INTERACTIVE_SELECTOR and the <li> is not either, so a click on the name
 * resolves no control and the text branch (clicks.ts:145) contributes nothing.
 * If a row is ever made clickable as a whole, THAT control is the one that needs
 * the marker - screens/Bookings.tsx:216 is the worked example.
 * ⛔ S28 (b) - THE SCREENSHOT CHANNEL - IS A RECORDED RESIDUAL, NOT A FIX, AND
 * IT MUST NOT BE READ AS ONE. `data-fb-opaque` is honoured ONLY by the click
 * recorder; the capture path's onCloneEachNode blanks the VALUE of cloned
 * INPUT / TEXTAREA / SELECT elements and reproduces rendered TEXT verbatim. So
 * the account names, towns and bill-to labels rendered below DO reach the JPEG.
 * What bounds it, measured rather than assumed:
 *   - the widget is build-gated: scripts/build-client.mjs resolves the host to a
 *     no-op stub unless ECMB_FEEDBACK_KIOSK=1 AND ECMB_TEST_DEPLOY=1, so without
 *     both flags the capture code is not in the module graph at all.
 *     ⚠️ THAT IS A CODE PROPERTY, NOT A DEPLOYMENT GUARANTEE, and the difference
 *     matters: `npm start` is `build:client && tsx src/bin/serve.ts`, so the
 *     posture is chosen AT CONTAINER START from the service's variables. The
 *     port-the-widget plan records ECMB_TEST_DEPLOY as "inert on the kiosk" and
 *     warns that "a shared Railway variable group would collapse the conjunction
 *     to one variable". Treat the gate as one variable-group edit away from ON.
 *   - the capture filter's own marker attribute is barred as the mitigation:
 *     scripts/check-killswitch.mjs greps the built client, and the served
 *     sourcemap embeds these comments, so even naming the literal fails the gate.
 * ⇒ THE RESIDUAL: on a build with the widget enabled, a capture taken on this
 * screen contains the customer account names, towns and payer names it renders -
 * and, since T1719 M5, an account's SHORT CODE, which v40's column comment rules
 * IDENTIFIES THE HOME. ⚠️ NOT through the field: the capture blanks the VALUE of a
 * cloned input, so the box itself is covered. It is the RENDERED TEXT that carries
 * it - the post-save banner reads "Account added. Its short code is KLNH." beside
 * the list of names, which is a code and a home in one frame.
 * ⚠️ WHAT WOULD CLOSE IT, stated accurately because the first version of this
 * paragraph overstated the barrier and an overstated barrier invites indefinite
 * deferral: the fix is to extend the capture filter, which lives in
 * src/client/feedback - a directory generated by scripts/sync-feedback.mjs from
 * the copy under site/. That is NOT a cross-repo change: site/ is one of four
 * packages in THIS git tree. The barrier is THIS MILESTONE'S FILE-OWNERSHIP
 * BOUNDARY plus `sync:feedback:check`, which reds if the generated copy is
 * edited directly. Edit the site copy, re-sync, and it lands.
 *
 * ⭐ AND THE CONSENT ITEM THIS MILESTONE MATERIALLY CHANGES, which is the part a
 * decision-maker needs: docs/plans/inline-2026-07-29-port-feedback-widget-to-
 * kiosk-21bb.md carries an open `PENDING - ALEX` item asking for "an explicit
 * nod that kiosk screenshots may carry customer PII". When it was raised, the
 * kiosk held synthetic fixtures. This milestone seeds TWO NAMED REAL NURSING
 * HOMES with their trading addresses and Eircodes, so the item's subject has
 * changed from test data to identifiable customer organisations. ⇒ It needs
 * RE-RAISING on that basis; it must not be closed against the old subject.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ApiAccount, ApiBillingParty } from '../../lib/actions.js';
import {
  createAccount,
  getBillingAddress,
  getBillingEmail,
  setAccountActive,
  setBillingAddress,
  setBillingEmail,
  setInvoiceResidentNames,
  setUpBankDetails,
  updateAccount,
  type AccountInput,
  type AccountResult,
  type BankDetails,
  type BillingAddress,
  type BillingEmailTarget,
} from '../../lib/account-actions.js';
import { Button, EmptyState, FactsTable, FormField } from '@ecmb/ui';
import { Banner } from '../../components/Banner.js';
import type { RegistryErrorCode } from '../../../shared/registry-error-schema.js';

/**
 * ⛔⛔ THE REFUSALS THAT ARE **ABOUT THE SHORT CODE BOX** (T1719 M6, review
 * batch I) - the set that marks that one input invalid, and nothing else.
 *
 * ⛔ KEYED ON THE CODE, NEVER ON THE SENTENCE. Every one of these arrives as a
 * `code` on the response body, which `account-actions.ts` already narrows
 * through `registryErrorCodeOf` and now hands back beside its copy. Matching
 * the rendered sentence instead would tie a screen-reader affordance to
 * wording, so an ordinary copy edit would silently un-mark the field.
 * ⛔ AND IT IS A SET, NOT `code.startsWith('short_code')`. A prefix test would
 * silently adopt any future `short_code_*` member - including one that is not
 * about this box at all - and the exhaustive `Record<RegistryErrorCode, ...>`
 * maps in `account-actions.ts` are the repo's convention for exactly this.
 * ⚠️ `short_code_conflict` and `short_code_locked` are 409s; the other three are
 * 400s. The status is irrelevant here - what matters is WHICH FIELD refused.
 */
const SHORT_CODE_REFUSALS: ReadonlySet<RegistryErrorCode> = new Set<RegistryErrorCode>([
  'short_code_shape',
  'short_code_clinical',
  'short_code_reserved',
  'short_code_conflict',
  'short_code_locked',
]);

export type AccountListProps = {
  accounts: ApiAccount[];
  /** The bill-to choices. Kept beside the accounts rather than fetched here:
   *  one screen entry, one round of reads, one degrade posture. */
  billingParties: ApiBillingParty[];
  /**
   * True when an empty `accounts` might NOT mean "there are none" — the read
   * failed, or it has not resolved yet. Suppresses the empty-state sentence,
   * because AccountBook is saying the honest thing above instead.
   *
   * ⛔ ABSENT MEANS THE CALLER HAS **PROVEN** THIS LIST IS REALLY EMPTY. Any call
   * site rendering this list from a read that can fail must pass it. Optional
   * only so the shipped spec harness compiles unchanged; a second production
   * call site that forgets it silently reinstates the defect (T698), and `tsc`
   * will not say a word. AccountBook.tsx owns this contract.
   *
   * ⚠️ KEYED ON THE ACCOUNTS READ ALONE, not on whatever raised the banner. A
   * failed billing-parties read makes this TAB incomplete without making this
   * LIST's emptiness a lie.
   */
  emptinessUnknown?: boolean;
  onChanged: () => void;
  /** ⭐ board 2812 M4 (plan step 3, a home's Details): the list holds that home's one
   *  account, and the form appears only while it is being edited (no Add form). */
  editOnly?: boolean;
};

const EMPTY_FORM: AccountInput = {
  name: '',
  addressLine: null,
  town: null,
  county: null,
  eircode: null,
  groupName: null,
  kind: null,
  phone: null,
  contactName: null,
  vatNumber: null,
  billToId: null,
  /**
   * ⛔⛔ `undefined`, NOT `null`, AND EVERY OTHER OPTIONAL FIELD HERE IS `null` —
   * SO THE HOUSE SHAPE IS THE TRAP.
   *
   * The route's contract (pinned in test/server/account-write-routes.spec.ts):
   * the key ABSENT means "derive a code from the name"; an explicit `null` or a
   * blank means NO CODE. `JSON.stringify` DROPS an `undefined` value, so this is
   * how the Add form asks for a derived code. A `null` here would suppress the
   * derivation for every new account, invisibly, because a code-less account
   * looks exactly like an ordinary one.
   * ⚠️ AND THIS LINE ALONE DOES NOT CLOSE IT. The box is a controlled input:
   * typing one letter and deleting it leaves `''`, which the route also folds to
   * "no code". The Add/Edit mapping is therefore made in `onSave`, per route.
   */
  shortCode: undefined,
};

/**
 * ⭐ THE POST-SAVE SENTENCE, AND THE THIRD ANSWER IS "SAY NOTHING EXTRA".
 *
 * The form returns to Add after a save (`backToAdd`), so without this a DERIVED
 * code would first be seen on the next Edit and a WITHHELD one never. `run`
 * chooses its notice BEFORE the action runs, so the save closure composes this
 * and hands it back as `AccountResult.text`.
 * ⛔ `undefined` MEANS THE SERVER SAID NOTHING ABOUT THE CODE (one that predates
 * M5), which is NOT the same as "no code could be made" — so it falls back to
 * the plain sentence rather than asserting an absence nobody reported.
 * ⛔ ASCII only: `check:copy` grades this region.
 */
const accountAddedSentence = (shortCode: string | null | undefined): string | undefined => {
  if (shortCode === undefined) return undefined;
  return shortCode === null
    ? 'Account added. No short code could be made from the name, so open it and give it one.'
    : `Account added. Its short code is ${shortCode}.`;
};

/**
 * The four organisation kinds, with the words an operator reads.
 *
 * ⚠️ THE TOKENS ARE RE-DECLARED HERE RATHER THAN IMPORTED, exactly as
 * foldAccountName below is re-declared and for the same reason: they are
 * exported by core/data/accounts-repo.ts, which imports `pg`, and no file under
 * src/client imports from src/core at all. What is NOT left to drift is the
 * SET - account-list.spec.tsx pins these four values against that module's
 * ACCOUNT_KINDS, so a fifth kind added on the server side reds here rather than
 * quietly offering an option the route refuses.
 *
 * ⛔ THE LABELS ARE NOT THE TOKENS AND MUST NOT BE. The stored value is what the
 * database CHECK carries; these are copy, and copy on this screen is rewritten
 * whenever the operator finds a word clearer.
 *
 * ⛔ AND THE FIRST OPTION IS NOT 'other'. An unset kind means NOBODY HAS
 * CLASSIFIED THIS ACCOUNT YET; 'other' means somebody looked and it is none of
 * the three. Defaulting the control to 'other' would record a decision that was
 * never made.
 */
const ACCOUNT_KIND_CHOICES: readonly { value: string; label: string }[] = [
  { value: 'nursing-home', label: 'Nursing home' },
  { value: 'school', label: 'School' },
  { value: 'society', label: 'Society or club' },
  { value: 'other', label: 'Something else' },
];

/** What the list shows in a row for an account that has been classified. An
 *  unrecognised token renders as itself rather than vanishing: a value the
 *  database holds and this screen cannot name is worth seeing. */
const kindLabel = (kind: string): string =>
  ACCOUNT_KIND_CHOICES.find((c) => c.value === kind)?.label ?? kind;

/**
 * What GET /api/billing-email answered for the account being edited.
 *
 * ⛔ A UNION RATHER THAN A VALUE PLUS TWO FLAGS, because the four states are not
 * interchangeable and three of them BLOCK THE WRITE. `none` is the add form
 * (there is no account to read yet), `loading` is the round trip, `failed` means
 * the read did not answer - and in all three there is no `rollUpKey`, so posting
 * an address would be posting it UNGUARDED at whichever payer the resolver finds
 * at write time. Only `read` carries the token that makes the server's
 * payer-moved refusal work.
 */
type BillingRead =
  | { state: 'none' }
  | { state: 'loading' }
  | { state: 'failed'; message: string }
  | {
    state: 'read';
    target: BillingEmailTarget;
    rollUpKey: string;
    billingEmail: string | null;
    /** The payer this address was resolved against, so a save can tell whether
     *  the operator has since repointed the form at a different one. */
    billToId: number | null;
    /** ⭐ board 3059 - the payer's resident-names switch, as the read answered it. */
    residentNames: boolean;
  };

/**
 * Board 2764 M1 - what GET /api/billing-address answered. Read only when the
 * billing email read said the account bills to a PARTY; an account that bills
 * itself is addressed with its own address fields above, so it has no group.
 * Only `read` carries the token, so only `read` lets a save post an address.
 */
type AddressRead =
  | { state: 'none' }
  | { state: 'loading' }
  | { state: 'failed'; message: string }
  | { state: 'read'; rollUpKey: string; address: BillingAddress };

/** The four boxes, as controlled-input strings. */
type AddressBox = { addressLine: string; town: string; county: string; eircode: string };
const EMPTY_ADDRESS_BOX: AddressBox = { addressLine: '', town: '', county: '', eircode: '' };
const ADDRESS_KEYS = ['addressLine', 'town', 'county', 'eircode'] as const;
const ADDRESS_READ_FAILED = 'Could not read the billing address. Try opening the account again.';

/**
 * ⭐ board 2764 M7 (M2 ux-review Medium) - an IBAN in groups of four, the way a
 * bank prints it ("IE64 IRCE 9205 0112 3456 78"), so it can be read aloud and
 * checked against Stripe's letter. ⛔ COPYING GIVES THIS SPACED FORM, which is
 * the IBAN standard's own printed form (ISO 13616); it is the SAME characters as
 * Stripe's answer with spaces between groups, and nothing else is changed.
 */
export function ibanForReading(iban: string): string {
  return iban.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * The name fold the duplicate pre-check uses, matching the one the server
 * compares with (core/data/accounts-repo.ts normaliseAccountName, then a
 * lower-case comparison in SQL).
 *
 * ⚠️ RE-DECLARED RATHER THAN IMPORTED, exactly as Operators.tsx re-declares its
 * two folds and for the same reason: the repo module pulls in `pg` and would
 * drag a node-only dependency into the browser bundle. Drift can only ever cost
 * the operator a round trip - the server's 409 stays authoritative, so a fold
 * that disagrees can never turn a refusal into an accept.
 */
const foldAccountName = (raw: string): string =>
  raw.normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();

/** A nullable column read into a controlled input. The server does the reverse
 *  (a blank string collapses back to NULL), so a town typed as spaces is an
 *  absent town rather than a stored blank the list then renders as a gap. */
const toField = (raw: string | null): string => raw ?? '';

/**
 * ⭐ T1723 M3 (II-admin-F1). The id the refused reason field points its
 * `aria-describedby` at, and the id the refusal banner then carries.
 *
 * ⛔ A MODULE CONSTANT, NOT A LITERAL AT TWO SITES: the whole mechanism is that
 * the two spellings match, and two literals is exactly how they stop matching.
 * Same shape as `RateList.tsx`'s `STRIP_ERROR_ID`.
 */
const REASON_ERROR_ID = 'account-reason-error';
/**
 * ⭐⭐ ONE DECLARATION OF THE REASON REFUSAL (T1749 M7, the sixth fold). It is
 * rendered TWICE now, by design: once in the banner, which keeps `role="alert"`
 * and interrupts, and once in the field's own slot under the box. Holding it in
 * one constant is what stops the two drifting into two sentences about one
 * refusal, which is the shape `REGISTRY_ERROR_COPY` already keeps for the short
 * code's five.
 * ⚠️ AND THE BANNER OUTLIVES THE FIELD SLOT, BY DESIGN: typing into the box
 * (`onInput` below) calls `setRefusedField(null)` and NOT `setNotice(null)`, so
 * the field's own slot clears — the `aria-invalid` red border and its inline
 * copy drop as the operator returns to the box — while the `role="alert"` banner
 * keeps the last attempt's refusal. Interrupting is the one thing only the
 * banner does, and a refusal the operator has not yet resolved should not vanish
 * the instant she touches the field again; the banner is cleared on the next
 * save or exit (`setNotice(null)` at the run/reset sites), not on a keystroke.
 */
const REASON_REFUSAL = 'Say why, in a few words. It is kept with your name against this change.';

/**
 * ⭐ board 3059 - THE WHY BOX'S HINT WHILE THE "TURN ON" CONFIRM IS OPEN. The
 * compliance condition (Alex's ruling, Captain's /check, 9 Oct): names go on a
 * payer's invoices only once that payer has confirmed it in writing, so the
 * reason filed with the change is where that approval is recorded.
 */
const NAMES_ON_WHY_HINT = 'Say where the payer\'s written approval is recorded, for example the email it came in.';

/** The fieldset's state line: what the payer's invoices say now, and, for a
 *  party payer, that one setting covers every account billed to it. */
function residentNamesStateWords(on: boolean, target: BillingEmailTarget): string {
  const now = on
    ? 'On. Each line of this payer\'s invoices shows the resident\'s name.'
    : 'Off. This payer\'s invoices do not name the residents.';
  return target === 'billing-party'
    ? `${now} This setting belongs to the payer above, so it applies to every account billed to them.`
    : now;
}

/** The confirm's question. ⛔ The billing email is the one the form READ (the
 *  press refuses an unsaved one), so the sentence names the inbox the setting
 *  will actually reach. */
function residentNamesConfirmWords(on: boolean, billingEmail: string | null): string {
  const inbox = (billingEmail ?? '').trim() === '' ? 'the payer\'s billing email, once one is set' : (billingEmail ?? '').trim();
  return on
    ? 'Only turn this on when the payer has confirmed in writing that resident names may appear on its invoices. '
      // ⭐ board 3059: the privacy-notice sentence that stood here (security L3) is
      // withdrawn by Captain's /check. The notice itself was amended in the same
      // deploy (Q291, commit 4ae91517), so the condition no longer needs saying to Helen.
      + `Each line of this payer's invoices will show the resident's name. The invoices go to ${inbox}, and Stripe keeps a copy. `
      + 'Say where the written approval is recorded in the Why box below, then press Turn on.'
    : 'Each line of this payer\'s invoices will stop showing the resident\'s name, from the next invoice sent. '
      + 'Invoices already sent keep the names they went out with. Say why in the Why box below, then press Turn off.';
}

export function AccountList({
  accounts,
  billingParties,
  emptinessUnknown = false,
  onChanged,
  editOnly = false,
}: AccountListProps) {
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  /** null = the form is adding; a number = the form is editing that account. */
  const [editing, setEditing] = useState<number | null>(null);
  const [form, setForm] = useState<AccountInput>(EMPTY_FORM);
  /**
   * ⭐ THE OPERATOR'S REASON, ADDED BY T651 M4 AND REQUIRED ON ALL THREE WRITES.
   *
   * It is deliberately NOT part of AccountInput: that type is the account's
   * COLUMNS, and the reason is not one of them - it goes to a different table
   * entirely (the v27 lifecycle journal), beside who acted and when, inside the
   * same transaction as the change. Folding it into the form type would have
   * made it look like a stored field on `accounts`.
   *
   * ⚠️ S2 ASKED FOR THIS FROM THE START and M3 dissolved it for exactly one
   * reason - "a required reason with nowhere to be stored is a promise the
   * system does not keep". There is somewhere now.
   * ⚠️ AND THE FRICTION IS REAL AND IS ACCEPTED, not overlooked: these operators
   * bounced off a prior tool that was "too many buttons", and this is one more
   * field on a screen that already has seven. It earns its place because an
   * account rename silently changes what every quote and every future invoice
   * refers to, and "why" is the only part of that a later reader cannot
   * reconstruct.
   */
  const [note, setNote] = useState('');
  /**
   * ⛔ THE BILLING EMAIL IS ITS OWN PIECE OF STATE AND IS NOT IN `form`. The save
   * spreads `form` straight into the account update payload, whose reader
   * IGNORES UNKNOWN KEYS SILENTLY - so a billing email carried there would post,
   * be dropped, and report success. It also does not necessarily belong to this
   * account: see BillingRead above.
   */
  const [emailBox, setEmailBox] = useState('');
  const [billingRead, setBillingRead] = useState<BillingRead>({ state: 'none' });
  /** Board 2764 M1 - the billing party's postal address: its own state for the
   *  email's reason (it is not this account's columns). */
  const [addressBox, setAddressBox] = useState<AddressBox>(EMPTY_ADDRESS_BOX);
  const [addressRead, setAddressRead] = useState<AddressRead>({ state: 'none' });
  /** Board 2764 M2 - what the last "Set up bank details" press answered, for the
   *  account that is open. Cleared whenever the form opens another account. */
  const [bankDetails, setBankDetails] = useState<BankDetails | null>(null);
  /** ⭐ board 3059 - the open "Resident names on invoices" confirm: `true` asks
   *  to turn the switch on, `false` to turn it off, `null` is closed. */
  const [namesConfirm, setNamesConfirm] = useState<boolean | null>(null);
  /**
   * ⛔ WHETHER THE LAST SAVE WAS REFUSED **BY THE SHORT CODE BOX** (T1719 M6,
   * review batch I). It drives `aria-invalid` and the red border on that one
   * input, and it exists as state rather than as a read of `notice.text`
   * because a notice is prose: see `SHORT_CODE_REFUSALS` above.
   *
   * ⛔⛔ A SENTENCE, NOT A BOOLEAN, SINCE T1719 M7 (Captain's M6 gate, item 5).
   * M6 marked the box red and left the note under it saying the EDITABLE thing -
   * "it can change until the first reference is issued" - while the explanation
   * sat in a banner that can be scrolled off a docked iPad. So the field's own
   * message contradicted its own red border. It holds `result.message`, which is
   * the SAME object `run` puts in the banner (`REGISTRY_ERROR_COPY[code]`,
   * handed back by `account-actions.ts`), never a string matched or rebuilt
   * here: the copy map is module-private and the refusal CODE, not the prose, is
   * still what decides whether the box is marked at all.
   * ⚠️ `null` MEANS NOT REFUSED, and every read tests `!== null` rather than
   * truthiness - a sentence is always non-empty today, and a truthiness test
   * would silently unmark the box the day one is not.
   */
  const [codeRefused, setCodeRefused] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const noticeRef = useRef<HTMLDivElement>(null);
  /**
   * ⭐ T1723 M3 (II-admin-F1, F10) - THE TWO FIELDS THE SCREEN HAS TO BE ABLE TO
   * PUT THE CARET IN, and the flag that says the reason was refused.
   *
   * ⛔ THE FINDING: the refusal banner said "Say why..." and the `#acct-note` it
   * meant was measured 1510px below the fold, inside the Add form, with no
   * `aria-invalid` and no `aria-describedby`. One field, four actions, and
   * nothing on screen connected the sentence to the box. The second half is the
   * same defect one control over: `Edit` retitled that form ~2050px away with no
   * scroll and no focus, so the click looked like it had done nothing.
   *
   * ⛔ FOCUS, NOT `scrollIntoView` - `PriceList.tsx:327`'s standing ruling, and
   * this is the milestone that lifts it here rather than leaving it recorded as
   * a residual. Moving focus scrolls the control into view AS A SIDE EFFECT and
   * tells a screen-reader user where she now is; a bare scroll fixes only the
   * sighted half.
   */
  const noteRef = useRef<HTMLInputElement>(null);
  /** ⭐ T1749 M7, the seventh fold: the short-code box, so a refusal about it can
   *  land the caret ON it. See the two refusal paths below. */
  const codeRef = useRef<HTMLInputElement>(null);
  const rowNoteRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  /**
   * ⭐ T1723 M3 (ux-review HIGH) - WHICH ROW IS ASKING "ARE YOU SURE?". null =
   * none. PORTED VERBATIM FROM `DestinationList.tsx` AS IT STANDS AFTER TWO
   * REVIEW ROUNDS, rather than re-derived: the sibling screen shipped this
   * affordance three times before it was right, and re-deriving it here would
   * have been three chances to reproduce one of the leaks.
   *
   * ⛔ THE DEFECT: `Take out of use` fired on ONE TAP after `missingReason()`,
   * on every row, with no confirm and no undo, against DESIGN.md §5.1
   * ("Destructive ... confirm + undo"). The write is journalled and
   * append-only, so there is nothing to undo afterwards - which makes the
   * confirm the only place the operator can change her mind.
   */
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  /**
   * ⛔⛔ THE CONFIRM SWAP OWNS ITS REASON, AND THE ADD FORM OWNS ITS OWN. The
   * sibling screen shared one `note` between the two and produced the same leak
   * three times: a reason typed for row A reached row B's confirm pre-filled,
   * then reached BOTH `onSave` and another row's `Put back in use`, each POSTing
   * it against the wrong subject into a journal `core/roles.sql` REVOKEs UPDATE
   * and DELETE on. Two states here from the start.
   * ⭐ `PriceList.tsx:536` is the house precedent: a reason is bound to its
   * subject.
   */
  const [confirmNote, setConfirmNote] = useState('');
  /**
   * WHICH reason field a refusal is about, so the red border and the
   * `aria-describedby` land on the box actually being asked for. A boolean
   * marked BOTH renderings, which is a lie once they hold two values.
   */
  const [refusedField, setRefusedField] = useState<'form' | 'confirm' | null>(null);
  /**
   * Where the next notice should put the caret. A REF rather than state because
   * it is read inside the notice effect and must not itself schedule a render:
   * `null` keeps the existing behaviour (focus the banner), a field ref
   * overrides it for this one notice only.
   */
  const focusOnNotice = useRef<{ current: HTMLElement | null } | null>(null);
  /** Why focus is about to move into the FORM. Gated on an explicit reason
   *  rather than on `editing` changing, because `startAdd()` also runs after a
   *  successful save, where focus belongs on the status message and not back in
   *  the form. PriceList.tsx's shape, lifted here with its reasoning. */
  const pendingFormFocus = useRef<'edit' | null>(null);
  /**
   * Which account the open form belongs to, read SYNCHRONOUSLY. `editing` is
   * state and is stale inside the billing-email read's own continuation, so two
   * quick Edit clicks would race and the slower answer would land in the form
   * belonging to the other customer - one home's invoice address shown under
   * another home's name. Same reason `inFlight` below is a ref and not `busy`.
   */
  const openAccount = useRef<number | null>(null);
  // The double-submit latch, and it cannot be `busy`: `if (busy) return` reads a
  // value fixed at RENDER time and setBusy does not re-render before the next
  // click in the same task, so two fast taps both see false. A ref is written
  // and read synchronously. `busy` stays - greying the controls out is a
  // different job. (Operators.tsx measured this; the shape is copied.)
  const inFlight = useRef(false);

  // Where the caret goes after an action. The control that was clicked is
  // `disabled` while the request is in flight, and the browser blurs a disabled
  // element, so focus drops to <body> on every single action without this.
  useEffect(() => {
    if (notice === null) return;
    // ⭐ T1723 M3: a refusal that NAMES A FIELD lands on the field, not on the
    // banner. `role="alert"` on the banner still announces the sentence, so the
    // screen-reader user hears it either way; what changes is that the caret
    // (and therefore the viewport) ends up at the box she has to fill in.
    const field = focusOnNotice.current;
    focusOnNotice.current = null;
    if (field != null) { field.current?.focus(); return; }
    noticeRef.current?.focus();
  }, [notice]);

  /** II-admin-F10: `Edit` moves the caret into the form it just retitled. */
  useEffect(() => {
    const why = pendingFormFocus.current;
    pendingFormFocus.current = null;
    if (why === 'edit') nameRef.current?.focus();
  }, [editing]);

  /**
   * ⭐ WCAG 4.1.3 (ux-review) - OPENING THE CONFIRM ANNOUNCES ITSELF.
   *
   * ⛔ THE FINDING, measured on the sibling screen and true here by
   * construction: the swap replaces the row's buttons with a field and two new
   * ones, and the control that was clicked is unmounted by that very change, so
   * `document.activeElement` falls to `<body>`. A keyboard or screen-reader
   * operator is told nothing happened, on the one interaction whose entire
   * purpose is to ask her a question.
   * ⛔ FOCUS, NOT AN ARIA-LIVE ANNOUNCEMENT: this is a QUESTION with a required
   * answer, and the answer's box is where she has to be. Focus scrolls it into
   * view as a side effect and names it as she lands (`PriceList.tsx:327`).
   * ⚠️ Keyed on `confirmingId`, so it fires on OPEN and on a MOVE between rows,
   * and not on close (the guard) or on any unrelated render.
   */
  useEffect(() => {
    if (confirmingId === null) return;
    rowNoteRef.current?.focus();
  }, [confirmingId]);

  const run = async (action: () => Promise<AccountResult>, okText: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    // ⛔ CLEARED ON ENTRY, NOT ON SUCCESS. Every exit from here - a success, an
    // uncoded failure, a refusal about some other field - must leave the box
    // unmarked, and clearing at the one entry point is what makes that true for
    // an exit added later. Same rule, and the same reason, as `setNote('')` in
    // the `finally` below.
    setCodeRefused(null);
    try {
      const result = await action();
      if (!result.ok) {
        const code = result.code ?? null;
        const aboutTheCode = code !== null && SHORT_CODE_REFUSALS.has(code);
        // ⭐⭐ T1749 M7, the seventh fold — A REFUSAL ABOUT THE CODE LANDS ON THE
        // CODE BOX, which is the rule `missingReason` has followed since T1723 M3
        // and which this path never took. Measured at both planes by two
        // reviewers: focus went to the banner above the list while the box and
        // both its sentences sat 303 to 600px BELOW the fold, and a keyboard-only
        // operator needed 26 Tabs across 12 rows to reach the box again — a
        // distance that grows with the account book. The banner keeps
        // `role="alert"`, so the sentence is still announced; what changes is
        // where the caret, and therefore the viewport, ends up.
        // ⛔ SET BEFORE `setNotice`, because the focus effect reads this ref when
        // the notice changes and clears it as it fires.
        if (aboutTheCode) focusOnNotice.current = codeRef;
        setNotice({ kind: 'error', text: result.message });
        setCodeRefused(aboutTheCode ? result.message : null);
        return;
      }
      // ⭐ `okText` IS CHOSEN BEFORE THE ACTION RUNS, so an action that LEARNS
      // something worth saying (the short code the server derived) hands it back
      // as `text`. Every other caller returns none and keeps its own sentence.
      setNotice({ kind: 'ok', text: result.text ?? okText });
      // The house invalidation: refetch everything the screen shows, so the
      // price tab cannot keep naming an account this tab just renamed.
      onChanged();
    } finally {
      inFlight.current = false;
      setBusy(false);
      // ⛔⛔ THE ONE RESET, AND IT IS HERE BECAUSE THIS IS THE ONE PLACE ALL FOUR
      // ACTIONS PASS THROUGH (T651 M4 output gate, shard B - a CONFIRMED defect,
      // reproduced with a probe).
      //
      // THE DEFECT: `note` is ONE piece of screen state shared by the form AND
      // the two row buttons, and only three of the four exits reset it -
      // startAdd, startEdit and onSave's success branch. The row toggles call
      // their action directly and never reset. So: type a reason, take row A out
      // of use, then click row B WITHOUT RETYPING, and row B's write carried row
      // A's reason verbatim.
      //
      // ⛔ WHY THAT IS SERIOUS RATHER THAN UNTIDY: registry_events is APPEND-ONLY
      // WITH NO ERASURE PATH (core/roles.sql REVOKEs UPDATE and DELETE), so a
      // misattributed reason is PERMANENT - and it defeats the exact promise the
      // copy below makes, that the reason is kept against THIS change. An audit
      // trail that confidently attributes the wrong reason to the wrong subject
      // is worse than none, because it reads as authoritative. It is also the
      // highest-traffic path: taking a row out of use is one click.
      //
      // ⭐ THE RULE CHOSEN, AND IT IS STRONGER THAN "CLEAR ON SUCCESS": A REASON
      // IS CONSUMED BY EXACTLY ONE ATTEMPT, whatever the outcome. `finally`, not
      // the success branch. Clear-on-success would leave the SAME defect in a
      // narrower state - a failed attempt would still let row A's reason reach
      // row B - and "measured in four of five states" is not measured. The cost
      // is stated rather than hidden: after a server error the operator retypes
      // her sentence. That is seconds at a keyboard, against a permanent wrong
      // attribution on a table nothing can correct.
      //
      // ⛔ AND NOT A PER-ROW REASON INPUT, which was the alternative. The sharing
      // is deliberate and the label says so ("needed for adding, ... and taking
      // ... out of use"); one field for four acts is a simplicity decision on a
      // screen whose hard constraint is that the operators bounced off a tool
      // that was "too many buttons". A per-row field multiplies it by the row
      // count. The bug was never the sharing - it was an exit that did not
      // reset - so the fix belongs at the shared exit, where a FIFTH action
      // added later inherits it for free.
      setNote('');
      // ⭐ AND THE CONFIRM'S OWN REASON WITH IT, plus the swap. Both reasons,
      // not "whichever the act used": an attempt is a state change on the whole
      // screen, and a primed confirm still holding a sentence after some other
      // row was written is exactly the leak class the sibling screen produced
      // twice. Clearing the one it did not use costs a retype and can never
      // misattribute.
      // ⚠️ A REFUSAL IS NOT AN ATTEMPT: `missingReason` returns before `run` is
      // ever called, so the swap stays open around the field it is asking her
      // to fill in.
      setConfirmNote('');
      setRefusedField(null);
      setConfirmingId(null);
    }
  };

  /**
   * Close the confirm swap, or move it to another row. THE SINGLE CLOSE PATH
   * outside `run`'s `finally`, so a transition cannot forget one of the three
   * things it has to reset - which is how the sibling screen's leak happened.
   * ⛔ NOT called from that `finally`: it nulls the notice, which is the
   * operator's only report of what the write did.
   */
  const setConfirming = (accountId: number | null) => {
    setConfirmingId(accountId);
    setConfirmNote('');
    setRefusedField(null);
    setNotice(null);
  };

  /** Back to the add form, with nothing of the account just open left behind.
   *  ⛔ Including the billing read: a stale `read` state would let the add path
   *  send another account's roll-up key. */
  const backToAdd = () => {
    openAccount.current = null;
    setEditing(null);
    setForm(EMPTY_FORM);
    setEmailBox('');
    setBillingRead({ state: 'none' });
    setAddressBox(EMPTY_ADDRESS_BOX);
    setAddressRead({ state: 'none' });
    setBankDetails(null);
    setNamesConfirm(null);
    setCodeRefused(null);
  };

  const startAdd = () => {
    backToAdd();
    setNote('');
    // ⛔ AND THE NOTICE, THE REFUSAL FLAG AND THE SWAP WITH IT (T1723 M3 output
    // gate). `startAdd` is what `Stop editing` calls; it nulls the notice, so a
    // refusal flag that survived left `#acct-note` rendering
    // `aria-invalid="true"` and an `aria-describedby` pointing at a banner that
    // had just been removed from the DOM - a red box for an error that is gone,
    // and a dangling reference a screen reader resolves to nothing.
    setConfirming(null);
  };

  /**
   * One refusal for every action on this screen, so the form and the two row
   * buttons cannot disagree about what is required.
   *
   * ⛔ THE REASON IS PASSED IN, NEVER READ FROM STATE, and `which` names the box
   * it came out of. A gate that looked up "the reason" was a gate that could
   * approve one act on another act's sentence; the caller holds the binding.
   *
   * ⚠️ A REASON REFUSAL REPLACES A SHORT-CODE REFUSAL (T1719): the two are the
   * screen's only field-level refusals and they share the one banner, so the
   * code's sentence is cleared here rather than left standing beside a second
   * one. They cannot both be true of one save: the reason gate runs before the
   * POST, and the code refusal is the POST's own answer.
   */
  const missingReason = (which: 'form' | 'confirm', reason: string): boolean => {
    if (reason.trim() !== '') {
      setRefusedField(null);
      return false;
    }
    setCodeRefused(null);
    setRefusedField(which);
    focusOnNotice.current = which === 'confirm' ? rowNoteRef : noteRef;
    setNotice({ kind: 'error', text: REASON_REFUSAL });
    return true;
  };

  /**
   * ⭐ Board 2764 M2 (R-A3) - "SET UP BANK DETAILS" for the payer on screen.
   *
   * ⛔ ONLY AGAINST WHAT IS SAVED. The server sets up the payer, email and address
   * as they are STORED, under the key this form READ; a payer, email or address
   * typed but not saved would be ignored, and the details shown would belong to
   * something other than what the form says. So an unsaved billing change is
   * refused here, before anything is sent.
   * ⛔ THE REASON IS THE FORM'S OWN `Why` box, the one the billing email write
   * sends (`missingReason`); it is filed only when the press creates the payer's
   * record at Stripe.
   */
  const onSetUpBankDetails = () => {
    const accountId = editing;
    const read = billingRead;
    if (accountId == null || read.state !== 'read') return;
    const readAddress = addressRead.state === 'read' ? addressRead.address : null;
    const unsaved = read.billToId !== form.billToId
      || emailBox.trim() !== (read.billingEmail ?? '')
      || ADDRESS_KEYS.some((k) => addressBox[k].trim() !== (readAddress?.[k] ?? ''));
    if (unsaved) {
      setNotice({
        kind: 'error',
        text: 'Save the account first, then set up bank details. They are set up for the payer, '
          + 'billing email and billing address as they are saved.',
      });
      return;
    }
    // ⭐ board 2764 M7: the disabled attribute gates the pixel; this gates the
    // decision (a click dispatched at a disabled button still reaches here).
    if ((read.billingEmail ?? '').trim() === '') {
      setNotice({ kind: 'error', text: 'Add a billing email and save the account first.' });
      return;
    }
    if (missingReason('form', note)) return;
    const reason = note.trim();
    setBankDetails(null);
    void run(async () => {
      const result = await setUpBankDetails(accountId, read.rollUpKey, reason);
      if (!result.ok) return { ok: false, message: result.message };
      // The race guard startEdit uses: an answer for an account the form has left
      // is dropped, never shown under another account's name.
      if (openAccount.current === accountId) setBankDetails(result.details);
      return { ok: true };
    }, 'The bank details for this payer are shown under Bank details.');
  };

  /**
   * ⭐⭐ board 3059 - TURN THE PAYER'S RESIDENT NAMES ON OR OFF, from the open confirm.
   *
   * ⛔ ONLY AGAINST WHAT IS SAVED, `onSetUpBankDetails`' rule: the server sets the
   * payer as it is STORED, under the key this form read, and the confirm names
   * the billing email it read. A payer or email typed but not saved would make
   * that sentence name the wrong inbox, so it is refused before anything is sent.
   * ⛔ THE REASON IS THE FORM'S OWN `Why`, REQUIRED BOTH WAYS (`missingReason`),
   * and for ON its hint asks where the payer's written approval is recorded. The
   * server refuses a blank one too and files it in the history row.
   * ⚠️ The answer for an account the form has since left is dropped (the
   * `openAccount` race guard), and the confirm closes on every attempt.
   */
  const onConfirmResidentNames = () => {
    const accountId = editing;
    const read = billingRead;
    const on = namesConfirm;
    if (accountId == null || read.state !== 'read' || on === null) return;
    if (read.billToId !== form.billToId || emailBox.trim() !== (read.billingEmail ?? '')) {
      setNotice({
        kind: 'error',
        text: 'Save the account first, then change this setting. It is set for the payer and billing email as they are saved.',
      });
      return;
    }
    if (missingReason('form', note)) return;
    const reason = note.trim();
    void run(async () => {
      const result = await setInvoiceResidentNames(accountId, on, read.rollUpKey, reason);
      setNamesConfirm(null);
      if (!result.ok) return result;
      if (openAccount.current === accountId) {
        setBillingRead((prev) => (prev.state === 'read' && prev.rollUpKey === read.rollUpKey ? { ...prev, residentNames: on } : prev));
      }
      return { ok: true };
    }, on
      ? 'Resident names will now appear on this payer\'s invoices.'
      : 'Resident names will no longer appear on this payer\'s invoices.');
  };

  const startEdit = (account: ApiAccount) => {
    // ⭐ II-admin-F10. The form is ~2050px below the row that was clicked; before
    // this, `Edit` retitled it silently off-screen. `PriceList.tsx`'s ruling,
    // lifted: focus, never a bare scroll. Called after the state writes below so
    // the field it lands on is the one this edit just filled.
    openAccount.current = account.accountId;
    pendingFormFocus.current = 'edit';
    setEditing(account.accountId);
    setNote('');
    // An open confirm on ANOTHER row would leave two half-finished acts on
    // screen at once. Its reason does not share state with this form, so it is
    // discarded here rather than inherited.
    setConfirming(null);
    setForm({
      name: account.name,
      addressLine: account.addressLine,
      town: account.town,
      county: account.county,
      eircode: account.eircode,
      groupName: account.groupName,
      kind: account.kind,
      phone: account.phone,
      contactName: account.contactName,
      vatNumber: account.vatNumber,
      billToId: account.billToId,
      // ⛔⛔ THIS LITERAL IS EXPLICIT AND ELEVEN FIELDS LONG, NOT A SPREAD, so an
      // omitted OPTIONAL member COMPILES CLEAN — and the consequence is not
      // cosmetic. Every Edit would open with `form.shortCode === undefined`, the
      // box empty, and `onSave`'s Edit rule would then send `shortCode: null`:
      // an ordinary town correction would CLEAR the stored code, and on a locked
      // account it would 409 the whole edit. That is exactly the destruction the
      // route's absent-means-unchanged rule exists to prevent, arriving one layer
      // up as an explicit null the route is obliged to honour.
      shortCode: account.shortCode,
    });
    setEmailBox('');
    setBillingRead({ state: 'loading' });
    setAddressBox(EMPTY_ADDRESS_BOX);
    setAddressRead({ state: 'none' });
    setBankDetails(null);
    setNamesConfirm(null);
    setNotice(null);
    // ⭐ THE ONE PLACE THE BILLING EMAIL IS READ, AND IT IS AN EXPLICIT CALL
    // RATHER THAN AN EFFECT OR PART OF THE LIST LOAD. Once per Edit: never on
    // Add, never on render, and never for the other rows on screen. The list
    // route deliberately does not carry this field - three other consumers read
    // it and none of them has any use for who a customer's invoices go to.
    void (async () => {
      const read = await getBillingEmail(account.accountId);
      // ⛔ THE RACE GUARD. Two Edit clicks in a row, or an Edit followed by Stop
      // editing, and this answer can arrive after the form has moved on. Dropping
      // it is the only safe thing: applying it would put one customer's invoice
      // address in a form headed by another customer's name.
      if (openAccount.current !== account.accountId) return;
      if (!read.ok) {
        setBillingRead({ state: 'failed', message: read.message });
        return;
      }
      setBillingRead({
        state: 'read',
        target: read.target,
        rollUpKey: read.rollUpKey,
        billingEmail: read.billingEmail,
        billToId: account.billToId,
        residentNames: read.residentNamesOnInvoices,
      });
      setEmailBox(read.billingEmail ?? '');

      // Board 2764 M1 - the postal address, read only for a PARTY payer.
      if (read.target !== 'billing-party') return;
      setAddressRead({ state: 'loading' });
      const addr = await getBillingAddress(account.accountId);
      if (openAccount.current !== account.accountId) return;
      // ⛔ ONE TOKEN FOR BOTH LEGS. The save posts the address with the email
      // read's key, so an address read that resolved a different payer is not
      // shown: it would be one payer's address in a group labelled as another's.
      if (!addr.ok || addr.target !== 'billing-party' || addr.rollUpKey !== read.rollUpKey) {
        setAddressRead({ state: 'failed', message: addr.ok ? ADDRESS_READ_FAILED : addr.message });
        return;
      }
      setAddressRead({ state: 'read', rollUpKey: addr.rollUpKey, address: addr.address });
      setAddressBox({
        addressLine: addr.address.addressLine ?? '',
        town: addr.address.town ?? '',
        county: addr.address.county ?? '',
        eircode: addr.address.eircode ?? '',
      });
    })();
  };

  /** Whoever the form now says pays, in the words the operator picked them by.
   *  ⛔ Never the ACCOUNT's own name: the self-billing case says "this account"
   *  rather than putting a customer name in a notice that need not carry one. */
  const payerLabel = (billToId: number | null): string =>
    billToId == null
      ? 'this account'
      : (billingParties.find((b) => b.billingPartyId === billToId)?.name ?? 'the new payer');

  const onSave = () => {
    // ⛔ THE CLIENT-SIDE REFUSALS BELOW RETURN BEFORE `run`, so they never reach
    // its reset. Without this line a box marked by the SERVER's last refusal
    // would still be red while the banner above it talked about the name.
    setCodeRefused(null);
    const name = form.name.trim();
    if (name === '') {
      setNotice({ kind: 'error', text: 'The account needs a name before it can be saved.' });
      return;
    }
    // Answered before the round trip, from the list already loaded. The server's
    // 409 stays the authority for the race (two adds of one name in the same
    // second); this is the sentence that names the fix.
    // ⚠️ AHEAD OF THE REASON CHECK ON PURPOSE: a name that cannot be saved at all
    // is worth saying before asking the operator to justify saving it.
    const clash = accounts.some(
      (a) => a.accountId !== editing && foldAccountName(a.name) === foldAccountName(name),
    );
    if (clash) {
      setNotice({
        kind: 'error',
        text: 'There is already an account with that name. Use a name that tells the two apart, such as adding the town.',
      });
      return;
    }
    // ⭐ STEP 5 — THE SAME PRE-CHECK ONE FIELD ALONG, answered from the list
    // already loaded. Folded UPPERCASE after trimming, which is how the server
    // compares (`accounts_short_code_key` is case-sensitive and the route refuses
    // anything that is not already capitals). `foldAccountName`'s header records
    // why drift here is acceptable: "Drift can only ever cost the operator a
    // round trip - the server's 409 stays authoritative", so a fold that
    // disagrees can never turn a refusal into an accept.
    // ⚠️ AHEAD OF THE REASON CHECK, for the same reason the name clash is: a code
    // that cannot be saved at all is worth saying before asking her to justify
    // saving it.
    const codeBox = (form.shortCode ?? '').trim();
    const codeClash = codeBox !== '' && accounts.some(
      (a) => a.accountId !== editing
        && a.shortCode !== null
        && a.shortCode.toUpperCase() === codeBox.toUpperCase(),
    );
    if (codeClash) {
      // "Nothing was saved." IS PART OF THIS SENTENCE TOO (T1719 close-out,
      // Captain's gate item 3). This is the THIRD home of the conflict sentence:
      // the wire's (`app.ts`, `short_code_conflict`) and Helen's copy map
      // (`account-actions.ts`) both gained the clause at M7, and this pre-check
      // was the one copy still without it, so typing `klnh` against a stored
      // `KLNH` read differently from the same clash caught one round trip
      // later. It is true here in the plainest way: the clash is caught before
      // any request is posted. Pinned beside the other two homes in
      // `test/client/account-list.spec.tsx`.
      // ⛔⛔ AND IT MARKS THE FIELD, NOT ONLY THE FORM (T1749 M7, the fifth fold (`40ebaa24`), webapp
      // FAIL). The two refusal PATHS are one refusal to the operator: a malformed
      // code comes back from the server and marks the box red with its own
      // sentence under it, while a DUPLICATE was caught here, a round trip
      // earlier, and raised the banner alone — same mistake, same field, two
      // different screens. The client-side pre-check is a speed optimisation, and
      // an optimisation may not change what the operator sees.
      const clashText = 'Another account already uses that short code. Nothing was saved. Give this one different letters.';
      // ⭐ T1749 M7, the seventh fold — the same landing as the server-refused
      // path above. The two paths are one refusal to the operator, and the
      // pre-check exists only to save a round trip.
      focusOnNotice.current = codeRef;
      setNotice({ kind: 'error', text: clashText });
      setCodeRefused(clashText);
      return;
    }
    // The Add form's own box, never whatever a confirm swap is holding (T1723 M3).
    if (missingReason('form', note)) return;
    /*
     * ⛔⛔ THE BLANK BOX MEANS OPPOSITE THINGS ON THE TWO ROUTES, SO THE MAPPING
     * IS MADE HERE, PER ROUTE, AND SAID OUT LOUD AT EACH CALL SITE.
     *
     *  · ADD, blank  -> the key is ABSENT, and the server derives a code from the
     *    name. `EMPTY_FORM` leaves it `undefined` for this, but that alone is not
     *    enough: a box TYPED INTO AND THEN CLEARED holds `''`, which the route
     *    folds to "no code" and which would silently suppress the derivation. So
     *    the key is dropped from the body rather than sent empty.
     *  · ADD, typed   -> the trimmed value.
     *  · EDIT, blank  -> an explicit `null`, which CLEARS the stored code. That is
     *    a real operation and the only way to perform it.
     *  · EDIT, typed  -> the trimmed value. Re-posting the SAME code is not a
     *    change: `updateAccountOn` compares against the stored value precisely so
     *    a locked account stays editable in every other field.
     * ⛔ NOT UPPERCASED ON THE WAY OUT. Storing letters other than the ones she
     * typed is the thing this spine refuses to do everywhere; the route's 400
     * names the rule instead.
     */
    const { shortCode: _codeInForm, ...withoutCode } = form;
    const input: AccountInput = editing == null
      ? (codeBox === ''
        ? { ...withoutCode, name }
        : { ...withoutCode, name, shortCode: codeBox })
      : { ...withoutCode, name, shortCode: codeBox === '' ? null : codeBox };
    const target = editing;
    const reason = note.trim();
    const typed = emailBox.trim();
    const read = billingRead;

    /*
     * ⭐ THE BILL-TO RULE, AND IT HAS FOUR ARMS BECAUSE THE OBVIOUS TWO ARE BOTH
     * WRONG SOMEWHERE.
     *
     * The billing email does not necessarily belong to this account: where
     * `billToId` is set the server writes the PARENT'S row, and several accounts
     * may point at one parent (a group that pays centrally). So:
     *
     *  - a plain dirty check SKIPS the write when the payer changes and the box
     *    was never touched, and nothing at all attaches to the new payer, with
     *    no word said about it;
     *  - FORCING the write in that case is worse, not better: it would put this
     *    site's address onto the shared parent and redirect every other site's
     *    invoices, in a save where the operator typed no address.
     *
     * Hence: arm 1 posts, arm 2 declines AND SAYS SO, arms 3 and 4 refuse before
     * anything is saved. Arms 3 and 4 are refused a SECOND time by the server,
     * which compares the roll-up key and writes nothing if the payer moved. This
     * half is the friendly refusal, that half is the guarantee.
     */
    const payerMoved = read.state === 'read' && read.billToId !== form.billToId;
    const emailEdited = typed !== (read.state === 'read' ? (read.billingEmail ?? '') : '');
    // Board 2764 M1 - the address group follows the email's rules: posted only
    // when a box differs from what was READ, with the same key, never across a
    // payer change, and never without a read.
    const addrRead = addressRead;
    const readAddress = addrRead.state === 'read' ? addrRead.address : null;
    const typedAddress: BillingAddress = {
      addressLine: addressBox.addressLine.trim() || null,
      town: addressBox.town.trim() || null,
      county: addressBox.county.trim() || null,
      eircode: addressBox.eircode.trim() || null,
    };
    const addressEdited = ADDRESS_KEYS.some(
      (k) => (typedAddress[k] ?? '') !== (readAddress?.[k] ?? ''),
    );

    // ⛔ ARM 3. The intent is genuinely ambiguous: the box was filled from the
    // OLD payer and is labelled against it, so from the moment the picker moves
    // the screen no longer knows whose address is in it. Refused BEFORE the
    // account update, so nothing at all is committed.
    if (target != null && payerMoved && emailEdited) {
      setNotice({
        kind: 'error',
        text: 'This account now bills to a different payer, so the billing email cannot be changed in '
          + 'the same save. Save the change of payer first, then open the account again and set the '
          + 'address against the payer shown.',
      });
      return;
    }
    // ⛔ ARM 4, THE SAME HAZARD WITH LESS TO SEE. On Add there is no prior read,
    // so there is no address on screen, no label saying whose it is, and no key
    // to compare - the operator would be overwriting a shared parent's address
    // completely blind. A new account that bills ITSELF is safe and is allowed:
    // the write lands on the row just created.
    if (target == null && form.billToId != null && typed !== '') {
      setNotice({
        kind: 'error',
        text: 'A new account that bills to a payer cannot be given a billing email here. The address '
          + 'would go on the payer and change it for every account billed to them. Add the account '
          + 'first, then open it again to set the address.',
      });
      return;
    }
    // ⛔ NO READ, NO KEY, SO NO WRITE. Without the roll-up key the server's
    // payer-moved check is skipped and the address goes wherever the resolver
    // points at write time. The box is disabled in these states, so this is a
    // backstop rather than a path - and it is the backstop that keeps the rule
    // true if the box is ever enabled while the read is in flight.
    if (target != null && emailEdited && read.state !== 'read') {
      setNotice({
        kind: 'error',
        text: 'The billing email for this account could not be read, so it cannot be changed here yet. '
          + 'Open the account again.',
      });
      return;
    }
    // ⛔ ARM 3 FOR THE ADDRESS: the group was filled from the OLD payer.
    if (target != null && payerMoved && addressEdited) {
      setNotice({
        kind: 'error',
        text: 'This account now bills to a different payer, so the billing address cannot be changed in '
          + 'the same save. Save the change of payer first, then open the account again and set the '
          + 'address against the payer shown.',
      });
      return;
    }
    // ⛔ NO READ, NO KEY, SO NO WRITE. The boxes are disabled in these states.
    if (target != null && addressEdited && (addrRead.state !== 'read' || read.state !== 'read')) {
      setNotice({ kind: 'error', text: `${ADDRESS_READ_FAILED} Nothing was saved.` });
      return;
    }

    // Every sentence below leads with what DID land: the account write commits
    // first and there is no rollback, so a failure on the second leg must not
    // read as "nothing was saved" and send the operator back to retype it all.
    const savedClause = target == null ? 'The account was added' : 'The account was saved';
    // ⛔ ARM 2 IS A THIRD FIXED SUCCESS SENTENCE, chosen HERE rather than after
    // the call, because `run` picks its notice before the action runs and this
    // is where whether the payer moved is known. It has to be said out loud: a
    // save that correctly declined to move an address is otherwise
    // indistinguishable from one that quietly did nothing.
    const okText = target == null
      ? 'Account added.'
      : payerMoved
        ? `Account saved. The billing email was not moved, so set one for ${payerLabel(form.billToId)} `
          + 'when you have it.'
        : 'Account saved.';

    void run(async () => {
      if (target == null) {
        const created = await createAccount(input, reason);
        if (!created.ok) return created;
        if (typed !== '') {
          if (created.accountId == null) {
            return {
              ok: false,
              message: `${savedClause}, but the billing email was not applied. `
                + 'Open the account and set it there.',
            };
          }
          // `account:<id>` is the key the resolver answers for an account that
          // bills itself, and arm 4 has already refused the case where it does
          // not. If a payer somehow resolves anyway the keys disagree and the
          // server refuses, so this arm carries the same guarantee as the rest.
          const attached = await setBillingEmail(
            created.accountId, typed, reason, `account:${String(created.accountId)}`, savedClause,
          );
          if (!attached.ok) return attached;
        }
        backToAdd();
        // ⛔ THE SENTENCE IS COMPOSED HERE AND NOWHERE ELSE. `run` returns void,
        // so the call site cannot read the code off it; returning `shortCode` on
        // the wire and composing nothing type-checks clean and renders the
        // sentence NEVER - the exact posts-is-dropped-reports-success shape
        // account-write-routes.spec.ts:1090-1095 exists to catch.
        return { ok: true, text: accountAddedSentence(created.shortCode) };
      }

      const saved = await updateAccount(target, input, reason);
      if (!saved.ok) return saved;
      // ⭐ ARM 1. Second, and only when the box differs from what was READ - not
      // from what the list happened to hold, which does not carry this field at
      // all. An empty box against a stored address is a real operation: it
      // clears it.
      let emailWritten = false;
      if (emailEdited && !payerMoved && read.state === 'read') {
        const attached = await setBillingEmail(
          target, typed === '' ? null : typed, reason, read.rollUpKey, savedClause,
        );
        // ⚠️ Reported as a failure, which also skips the list refetch and holds
        // the form open. That is accepted rather than papered over: retrying the
        // whole save is safe, because BOTH writes carry a real-change guard and
        // file no audit row for an edit that changed nothing.
        if (!attached.ok) return attached;
        emailWritten = true;
      }
      // Board 2764 M1 - the address leg, third, with the SAME roll-up key the
      // email leg sent (the read refused to show an address read under another).
      if (addressEdited && !payerMoved && read.state === 'read' && addrRead.state === 'read') {
        const placed = await setBillingAddress(
          target, typedAddress, reason, read.rollUpKey,
          emailWritten ? 'The account and its billing email were saved' : savedClause,
        );
        if (!placed.ok) return placed;
      }
      backToAdd();
      return { ok: true };
    }, okText);
  };

  const closedCount = accounts.filter((a) => !a.active).length;
  // ⭐ THE READER OF `active`. A closed account is out of the default view, which
  // is what "hidden from new work" means on the one screen that has to be able
  // to put it back.
  const visible = showClosed ? accounts : accounts.filter((a) => a.active);

  const set = (patch: Partial<AccountInput>) => setForm((prev) => ({ ...prev, ...patch }));

  /**
   * The active-flag write both entry points perform, with THE REASON PASSED IN.
   *
   * ⛔ A PARAMETER RATHER THAN A LOOKUP, which is the shape the sibling screen
   * arrived at after a security review: while this read `note` off the screen,
   * `Put back in use` on row B could spend whatever row A's open confirm was
   * holding and write it against B, permanently, into a journal with no erasure
   * path. The caller is the only thing that knows which box the sentence came
   * out of (`PriceList.tsx:536`: a reason is bound to its subject).
   */
  const toggleActive = (a: ApiAccount, which: 'form' | 'confirm', reason: string) => {
    if (missingReason(which, reason)) return;
    void run(
      () => setAccountActive(a.accountId, !a.active, reason.trim()),
      a.active
        ? 'That account is no longer offered for new bookings. Everything already booked keeps it.'
        : 'That account can be used again.',
    );
  };

  /**
   * ⭐ THE ROW THE FORM IS OPEN ON, WHICH IS WHERE THE THREE SHORT-CODE FACTS
   * COME FROM. They are properties of what is STORED, not of what is in the box:
   * `shortCodeRefused` grades the stored code against today's denylist, and
   * `shortCodeLocked` is DD4's `next_job_seq > 1`, computed on the server so the
   * counter itself never crosses the wire. On the Add form there is no row and
   * all three are absent, which is the state the derive sentence belongs to.
   */
  const editedAccount = editing == null
    ? null
    : (accounts.find((a) => a.accountId === editing) ?? null);
  const codeLocked = editedAccount?.shortCodeLocked === true;

  return (
    <>
      {notice && (
        <Banner
          kind={notice.kind}
          /* ⛔ ONE ID, ALWAYS (T1749 M7, the sixth fold). This banner used to
             BORROW the reason field's error id whenever a refusal was live, so
             `aria-describedby="account-reason-error"` resolved to a sentence
             measured 2386 document px above the box it described. The refusal
             now has a slot beside its field and owns that id; the banner keeps
             what only a banner can do, which is interrupt. ⚠️ The id is also
             unique per screen again, which the form's own uniqueness arm grades. */
          id="acct-notice"
          elementRef={noticeRef}
          tabIndex={-1}
        >
          {notice.text}
        </Banner>
      )}

      {/* ⛔ THE SENTENCE IS ONLY SAFE WHEN THE EMPTINESS IS KNOWN — see the prop's
          own note. A failed or unresolved read supports neither arm, and
          AccountBook carries the honest line in that state (T698). */}
      {/* ⭐⭐ T1749 M7 rework 1 — the library's `EmptyState` (§7.1: a headline
          and `role="status"`, so the sentence is announced when it replaces the
          rows). The TWO ARMS stay exactly as they were: which of them is true is
          this screen's knowledge, and the component never decides it. */}
      {visible.length === 0 && !emptinessUnknown && (
        <EmptyState
          /* ⛔⛔ T1749 M9 rework 2 — THE TONE TERNARY IS GONE AND BOTH ARMS ARE
             NEUTRAL, which is now the component's default, so nothing is passed.
             Round 5 keyed the tone to the arm on a TRUE-ZERO test: rows hidden by
             the out-of-use filter were neutral, an empty book was §7.2's green.
             The sitting replaced that test with a harder one — is this emptiness
             GOOD NEWS? — and a price book with no customer accounts in it is not:
             it is absent trade, or setup nobody has done yet. Green survives only
             where emptiness means work is finished (`QueueView`) or no defect was
             found (`Triage`).
             ⚠️ WHAT THIS LOSES, stated so the next reader does not rediscover it:
             the two arms no longer differ in PAINT, only in WORDS. The sentence is
             still keyed to the predicate below, and it is now the only thing that
             tells the two facts apart. */
          headline={accounts.length === 0
            ? 'No customer accounts yet. Add the first one in the form below.'
            : 'Every account is out of use. Tick the box below to see them.'}
        />
      )}

      {/* II-admin-F14: `list-style: none` makes Safari + VoiceOver drop list
          semantics, so the rows stop being counted and announced as a list.
          `role="list"` restores them without touching `ul.op-list > li.op-row`,
          which four pins read. NursingHomes.tsx already does this on its three. */}
      <ul class="op-list op-list--accounts" role="list">
        {visible.map((a, rowIndex) => {
          /* ⛔ T1749 M7 rework 1 — the confirm swap's reason box is keyed by the
             row's POSITION, never by `accountId` (the M7 criterion: no field id
             from wire data). Only one swap is open at a time, but the id must be
             unique against every OTHER row's markup as well, and the position is
             this render's own fact rather than the server's. */
          const rowKey = `acct-row-${String(rowIndex)}`;
          // ⛔ NOT a bill-to resolver. This shows the LINK where there is one and
          // nothing where there is not. The RULE - the linked party where
          // bill_to_id is set, the account itself where it is NULL - is recorded
          // in the schema and resolves in ONE function that lands with the
          // invoicing build, its first real consumer. Do not grow that precedence
          // here.
          const billTo = billingParties.find((b) => b.billingPartyId === a.billToId);
          return (
            <li key={a.accountId} class="op-row">
              <span class="op-name">{a.name}</span>
              {/* ⭐ ux-review WARN 3: THE META IS ONE CELL, so the row is the same
                  four column grid as the Destinations and Prices tabs and the
                  actions line up. Before this the Accounts tab was still a flex
                  and its last buttons wandered over a 66px spread, because the
                  number of spans between the name and the buttons depends on the
                  DATA (a classification, a town, a payer, a closed marker: any
                  subset of four). Wrapping them makes the row's shape a property
                  of the SCREEN rather than of the account.
                  ⭐ THE READER OF `kind`, LANDED IN THE SAME CHANGE AS THE COLUMN.
                  This schema already carries flags nothing reads; a classification
                  nothing displays would be the same thing one table over, and the
                  operator would have no way to tell a classified account from an
                  unclassified one. NULL renders nothing, which is the honest
                  showing of "nobody has said yet". */}
              <span class="op-row-flag">
                {a.kind != null && a.kind !== '' && (
                  <span class="chip chip--quiet">{kindLabel(a.kind)}</span>
                )}
                {a.town != null && a.town !== '' && <span class="op-login">{a.town}</span>}
                {billTo != null && <span class="op-login">Billed to {billTo.name}</span>}
                {!a.active && <span class="chip chip--quiet">Not in use</span>}
              </span>
              {confirmingId === a.accountId ? (
                /* ⭐ THE CONFIRM SWAP, ported from DestinationList.tsx as it
                   stands after two review rounds (ux-review HIGH). DESIGN.md
                   §5.1 asks for confirm + undo on a destructive; this write goes
                   into an INSERT-only journal, so there is no undo afterwards
                   and the confirm is the only place she can change her mind.
                   ⛔ THE REASON IS THE SWAP'S OWN (`confirmNote`), never the Add
                   form's `note`. Sharing them is what made the sibling screen
                   leak one row's sentence into another act three times.
                   ⛔ A `div`, NOT A `span`, SINCE T1749 M7's seventh fold. The
                   swap now holds a `field-error` PARAGRAPH when the reason is
                   refused, and a paragraph inside a span is invalid HTML: the
                   parser closes the span at the paragraph, which would put the
                   refusal OUTSIDE the flex container the rule below lays out.
                   ⚠️ NOTHING PINNED THE ELEMENT NAME — no spec and no e2e test
                   names `span.op-row-confirm` — and the class, its grid column
                   and its flex rules are unchanged, so this is the tag only. */
                <div class="op-row-confirm">
                  {/* A2-pricelists-1: a VISIBLE label, not a placeholder plus an
                      aria-label. A placeholder disappears the moment she types,
                      which on a REQUIRED field is the worst moment to lose it. */}
                  <label class="field-label" for={`${rowKey}-out-note`}>
                    Why is it going out of use?
                  </label>
                  <input
                    id={`${rowKey}-out-note`}
                    class="login-input op-row-why"
                    type="text"
                    ref={rowNoteRef}
                    data-fb-opaque
                    autocomplete="off"
                    placeholder="Why is it going out of use?"
                    {...(refusedField === 'confirm' ? { 'aria-invalid': 'true', 'aria-describedby': REASON_ERROR_ID } : {})}
                    value={confirmNote}
                    onInput={(e) => { setRefusedField(null); setConfirmNote((e.target as HTMLInputElement).value); }}
                  />
                  {/* ⛔ THE SAME SLOT, ON THE OTHER HALF OF THE SAME RULE (T1749
                      M7, the sixth fold). One reason rule, four actions, two
                      fields: the Add form's and this confirm swap's. Both name
                      `REASON_ERROR_ID` through `aria-describedby`, and until the
                      sixth fold that id was worn by the BANNER, so whichever box
                      was refused pointed at a sentence at the top of the screen.
                      ⚠️ THE ID IS STILL UNIQUE: `refusedField` is `'form'` XOR
                      `'confirm'`, so exactly one of the two slots exists at a
                      time and the form's uniqueness arm keeps grading that. This
                      field has no standing hint of its own (its label asks the
                      question), so there is no hint-then-error order to keep. */}
                  {refusedField === 'confirm' && (
                    <p class="field-error" id={REASON_ERROR_ID}>{REASON_REFUSAL}</p>
                  )}
                  <button
                    type="button"
                    class="btn btn--quiet btn--destructive"
                    data-fb-opaque
                    disabled={busy}
                    onClick={() => toggleActive(a, 'confirm', confirmNote)}
                  >
                    Yes, take it out of use
                  </button>
                  <button
                    type="button"
                    class="btn btn--quiet"
                    data-fb-opaque
                    disabled={busy}
                    onClick={() => setConfirming(null)}
                  >
                    Keep it
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    class="btn btn--quiet"
                    data-fb-opaque
                    disabled={busy}
                    onClick={() => startEdit(a)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    class={a.active ? 'btn btn--quiet btn--destructive' : 'btn btn--quiet'}
                    data-fb-opaque
                    disabled={busy}
                    onClick={() => {
                      // ⛔ ONLY THE DESTRUCTIVE DIRECTION ASKS. Putting an account
                      // back in use is additive and reversible by the same
                      // control, so a confirm there would be ceremony (§5.1 asks
                      // for confirm + undo on the DESTRUCTIVE level only).
                      if (a.active) { setConfirming(a.accountId); return; }
                      // ⛔ THE ADD FORM'S BOX, named rather than looked up: this
                      // direction has no field of its own, so it must read THAT
                      // one and never a confirm swap open on some other row.
                      toggleActive(a, 'form', note);
                    }}
                  >
                    {a.active ? 'Take out of use' : 'Put back in use'}
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>

      {closedCount > 0 && (
        <p class="helper-note">
          <label class="checkbox-inline">
            <input
              type="checkbox"
              data-fb-opaque
              checked={showClosed}
              onChange={(e) => setShowClosed((e.target as HTMLInputElement).checked)}
            />
            Show accounts not in use
          </label>
        </p>
      )}

      {/* ⭐ A REAL SEPARATOR ELEMENT (T1723 M3, the K2 fix on its SIBLING screen,
          found by conformance.spec.tsx's arm (ii) on its first run). DESIGN.md
          §5.1: "destructive never adjacent to primary". `ul.op-list`'s
          nextElementSibling is `div.op-add` whenever `closedCount === 0`, whose
          first control is the GOLD `Add account` - so the last row's red `Take
          out of use` sat directly above the screen's one primary with nothing
          between them. DestinationList.tsx had the identical shape and K2 gave
          it the identical fix; the `<h3>` inside `.op-add` separates nothing (it
          is inside the block, not between the two) and the "Show accounts not in
          use" paragraph renders only when `closedCount > 0`. `.section-rule` is
          the class Done.tsx:467 already uses for exactly this job.
          ⚠️ An `<hr>` is not interactive, so the Accounts DEBT row in
          control-budget.spec.tsx stays at 21. */}
      {/* ⭐ board 2812 M4 (a home's Details): `editOnly` shows the form only once Edit is
          pressed, so a home's page offers its own account's fields and no Add form. */}
      {(!editOnly || editing != null) && (
        <>
        <hr class="section-rule" />
        <div class="op-add">
          <h3 class="screen-sub">{editing == null ? 'Add an account' : 'Edit this account'}</h3>

          {/* ⭐⭐ T1749 M7 rework 1 — `FormField` from here down. The label's `for`,
              the control id, the class contract and the hint and error slots come
              from one place now; `opaque` carries the mark the feedback recorder
              reads off the CONTROL, which is the prop the library grew for this
              adoption rather than a thing this screen could keep doing by hand. */}
          <FormField
            id="acct-name"
            label="Account name"
            value={form.name}
            opaque
            autoComplete="off"
            elementRef={nameRef}
            onInput={(v: string) => set({ name: v })}
          />

          {/* ⭐ THE SHORT CODE, PLACED HERE BECAUSE IT IDENTIFIES THE ACCOUNT.
              The attributes are the EIRCODE input's, which is the only sibling that
              is also a constrained uppercase token - including `data-fb-opaque`,
              which the opaque census asserts on every button, input and select.
              ⛔ `readonly`, NOT `disabled`, WHEN THE CODE IS LOCKED. There is no
              <form> element here (the body is built from `form` state), so both
              attributes would send the same thing and submission is not the reason.
              A disabled input is greyed, dropped from the tab order AND from the
              accessibility tree, and cannot be selected - and Helen has to be able
              to READ AND COPY a locked code down the phone. `readonly` keeps it
              legible and focusable while refusing edits.
              ⛔⛔ AND `readonly` ALONE IS INVISIBLE (ux-review, M5 gate). A readOnly
              input renders IDENTICALLY to an editable one, so the only signal that
              the code is locked was the sentence underneath - and an operator who
              clicks the box and types nothing learns it by failing. The sheet
              already has a read-only treatment: `.op-cred-value` fills its readOnly
              input with `--cream-50` "because the panel behind it is white, so a
              white field would have nothing but a hairline to say where the value
              starts and stops".
              ⚠️ THE TOKEN IS REUSED, THE WHOLE CLASS IS NOT, and that is measured
              rather than tidy: `.op-cred-value` also sets `font-family: --font-mono`,
              `letter-spacing`, `max-width: 24rem`, `padding: --space-3` and
              `min-height: --target-min` - all of which LOSE to nothing here and win
              over `.login-input` (it is later in the sheet at equal specificity), so
              stacking the two would make the box change HEIGHT, PADDING and TYPEFACE
              the moment it locks. A field that resizes between two states of itself
              is a worse answer than the one being fixed. `.login-input--locked`
              carries the fill and the cursor only, following this sheet's own
              modifier convention (`.login-input--euro`).
              ⛔ THE MESSAGE IS WIRED WITH `aria-describedby`, so a screen reader
              announces WHY the box refuses input at the moment it is focused. The
              sentence is one element with four states, so one id covers all of
              them.
              ⛔⛔ AND A REFUSED SAVE MARKS THE BOX ITSELF (T1719 M6, review batch
              I). The five short-code refusals each render a `role="alert"` banner
              at the TOP of the screen, above a list that can be a dozen rows long
              - so the thing that says what went wrong can be off-screen from the
              box it is about, and NOTHING at the field said which of eight inputs
              to fix. Three attributes, no new markup:
               · `aria-invalid="true"`, which is what the sheet's existing
                 `.login-input[aria-invalid="true"]` rule keys the red border off -
                 so the SIGHTED signal and the ASSISTIVE one are the same fact, and
                 neither can be added without the other;
               · the banner's id APPENDED to `aria-describedby`, so the box reads
                 out its own note AND the refusal. ⛔ Appended, never replacing:
                 the note carries the lock/code-less/editable states and is the
                 only description in four of the five cases.
               · cleared on the next keystroke (below), on leaving the form, and at
                 the top of `run` - a stale red box is a lie about the current
                 value.
              ⛔ THE STATE IS KEYED ON THE REFUSAL **CODE**, never on the sentence
              - see `SHORT_CODE_REFUSALS` at the top of this file. */}
          {/* ⭐⭐ T1749 M7 rework 1 — `FormField`, hint and error as its OWN props.
              The two slots this field hand-rolled across three folds are what the
              component renders anyway, and it derives their ids from the control's
              (`acct-short-code-hint`, `acct-short-code-error`), so the SEEN order
              (hint then error), the READ order (error first in
              `aria-describedby`) and the wiring itself stop being this screen's to
              keep right. The arms that graded all three are re-pointed, not deleted.
              ⚠️ `locked`, NOT `disabled`: a locked code must stay readable and
              reachable, which is §3.2's rule and the reason that prop exists.
              ⚠️ `spellcheck` / `autocapitalize`: a constrained uppercase token, the
              Eircode's attributes, carried through the library rather than lost. */}
          <FormField
            id="acct-short-code"
            label="Short code"
            value={toField(form.shortCode ?? null)}
            opaque
            autoComplete="off"
            autocapitalize="characters"
            spellcheck={false}
            maxLength={5}
            locked={codeLocked}
            elementRef={codeRef}
            hint={
              editedAccount?.shortCodeRefused === true
                ? 'This code is a word that must not go on an invoice. Change it here. If the box '
                  + 'will not let you type, a job reference has already been issued or set aside for '
                  + 'the homes\' portal under it, so ask Alex.'
                : codeLocked
                  ? 'A job reference has already been issued or set aside for the homes\' portal with '
                    + 'this code, so it cannot change. It is here to read and copy. If it really has to '
                    + 'change, ask Alex.'
                  : editing != null && editedAccount?.shortCode === null
                    ? 'No code could be made for this account, so its jobs get a random reference '
                      + 'instead of one starting with its letters. Give it two to five letters here. '
                      + 'The name gave too few, or another account already uses the letters it would '
                      + 'have had.'
                    : editing == null
                      ? 'Two to five capital letters. Blank makes one from the account name.'
                      : 'This goes on this account\'s job references. It can change until the first '
                        + 'reference is issued or set aside for the homes\' portal under it. Clearing '
                        + 'the box leaves the account with no code.'
            }
            error={codeRefused ?? undefined}
            onInput={(v: string) => {
              // The refusal was about the value that WAS in the box. One keystroke
              // later it is about nothing, so the mark comes off before the next
              // save rather than after it.
              setCodeRefused(null);
              set({ shortCode: v });
            }}
          />

          {/* ⛔ THE EMPTY OPTION IS NOT 'other' AND THE TWO ARE NOT THE SAME
              ANSWER. Blank means nobody has classified this account yet; "Something
              else" means somebody looked and it is none of the three. Both real
              production accounts are unclassified today, so a default would have
              recorded a decision nobody made. */}
          {/* ⚠️ `.venue-select` IS NOT CARRIED, and it costs nothing: that class is
              `width: 100%`, which `.op-add .login-input` already gives every control
              in this form through a DESCENDANT selector that still reaches through
              `FormField`'s wrapper. A screen may not hand a component a class
              (§5.1), and here it does not need to. */}
          <FormField
            id="acct-kind"
            label="Organisation type"
            kind="select"
            opaque
            value={form.kind ?? ''}
            options={[
              { value: '', label: 'Not said yet' },
              ...ACCOUNT_KIND_CHOICES.map((c) => ({ value: c.value, label: c.label })),
            ]}
            onInput={(v: string) => set({ kind: v === '' ? null : v })}
          />

          <FormField
            id="acct-address"
            label="Street address"
            value={toField(form.addressLine)}
            opaque
            autoComplete="off"
            onInput={(v: string) => set({ addressLine: v })}
          />

          <FormField
            id="acct-town"
            label="Town or village"
            value={toField(form.town)}
            opaque
            autoComplete="off"
            onInput={(v: string) => set({ town: v })}
          />

          <FormField
            id="acct-county"
            label="County"
            value={toField(form.county)}
            opaque
            autoComplete="off"
            onInput={(v: string) => set({ county: v })}
          />

          {/* ⚠️ `spellcheck` AND `autocapitalize` SURVIVE THE ADOPTION because the
              library grew them for this class of field: an Eircode is a constrained
              uppercase token, so a spellchecker underlines every value it can hold. */}
          <FormField
            id="acct-eircode"
            label="Eircode"
            value={toField(form.eircode)}
            opaque
            autoComplete="off"
            autocapitalize="characters"
            spellcheck={false}
            onInput={(v: string) => set({ eircode: v })}
          />

          <FormField
            id="acct-group"
            label="Group"
            value={toField(form.groupName)}
            opaque
            autoComplete="off"
            hint={'Who to ring at renewal, if this one belongs to a wider group. It is a label for '
              + 'reading, not who the invoice goes to.'}
            onInput={(v: string) => set({ groupName: v })}
          />

          {/* ⛔ THE ORGANISATION'S NUMBER, KEPT AS TYPED AND NEVER USED TO MATCH
              ANYBODY. The number that identifies a PERSON lives on the customer
              record and is normalised there; this one is a switchboard an operator
              rings, so it may carry an extension, and normalising it would throw
              that away. */}
          <FormField
            id="acct-phone"
            label="Phone"
            type="tel"
            value={toField(form.phone)}
            opaque
            autoComplete="off"
            onInput={(v: string) => set({ phone: v })}
          />

          <FormField
            id="acct-contact"
            label="Contact person"
            value={toField(form.contactName)}
            opaque
            autoComplete="off"
            hint="Who to ask for when you ring. Clearing this box is how their name is removed."
            onInput={(v: string) => set({ contactName: v })}
          />

          {/* ⛔ STORED, AND NEVER PRINTED ON AN INVOICE. This operator is not
              VAT-registered and passenger transport is exempt, and Revenue holds an
              unregistered trader who shows VAT on a document liable for the amount
              shown. Keeping the customer's number is fine; putting it on the
              document is not, and the invoice screen has a pin that says so. */}
          <FormField
            id="acct-vat"
            label="VAT number"
            value={toField(form.vatNumber)}
            opaque
            autoComplete="off"
            autocapitalize="characters"
            spellcheck={false}
            onInput={(v: string) => set({ vatNumber: v })}
          />

          {/* ⛔ THE BILL-TO PICKER, AND THE FIRST OPTION IS THE POINT OF IT. An
              account with no parent payer stores NOTHING, not a stand-in row: the
              owner ruling behind the nullable column is that a customer who pays
              for itself must be representable without inventing a group for it.
              The list offers only payers that are in use; a retired payer stays
              attached to whichever accounts already point at it. */}
          <FormField
            id="acct-bill-to"
            label="Bills to"
            kind="select"
            opaque
            value={form.billToId == null ? '' : String(form.billToId)}
            options={[
              { value: '', label: 'This account pays for itself' },
              ...billingParties
                .filter((b) => b.active || b.billingPartyId === form.billToId)
                .map((b) => ({ value: String(b.billingPartyId), label: b.name })),
            ]}
            onInput={(v: string) => set({ billToId: v === '' ? null : Number(v) })}
          />

          {/* ⭐ THE BILLING EMAIL, AND THE LINE UNDER IT IS HALF THE FEATURE. The
              address belongs to whoever PAYS, which is the picker above, so one
              unlabelled box would stand for two different identities: this account,
              or a parent that several sites share. The invoice build refuses to
              issue without one, and until now no screen could set one, so its
              refusal pointed at a box that did not exist.
              ⛔ DISABLED UNTIL THE READ ANSWERS. An empty box that has not been
              read yet looks exactly like an address that is not set, and typing
              into it would post an address with no key to compare against the
              payer. */}
          {/* ⭐⭐ T1749 M7 rework 1 — `FormField`, and the FOUR BRANCHES ARE SPLIT
              BETWEEN TWO SLOTS. `state: 'none'` — the only branch a BLANK add form
              shows — is a hint by content (what the box is for, what shape the
              answer takes), so it is the component's `hint` and renders inline at
              hint size. The other three REPORT STATE (where the address is kept once
              the read answered, that it is being read, why the read failed) and
              render in the sibling `<p>` below, which the field names through
              `describedBy` so a reader still hears it.
              ⛔ THE SIBLING IS `.field-hint`, NOT `.helper-note` (Captain's M7 review,
              rework 2): `.helper-note` is the empty-LIST class (empty rosters, empty
              search), and a control's own state line is not an empty list — it is a
              caption-sized line under the box, which is what `.field-hint` is, the
              same re-class `DestinationList`/`PriceList` took one file over. The
              Q139 census stays honest with no split needed: the sibling renders only
              when `state !== 'none'`, so a blank add-form mount shows five hints and
              no state line (account-list.spec.tsx's `.op-add` census). */}
          <FormField
            id="acct-billing-email"
            label="Billing email"
            type="email"
            value={emailBox}
            opaque
            autoComplete="off"
            spellcheck={false}
            disabled={billingRead.state === 'loading' || billingRead.state === 'failed'}
            hint={billingRead.state === 'none'
              ? 'Where invoices go. An account that bills to a payer uses the address held on '
                + 'that payer, so add the account first and set it when you open it again.'
              : undefined}
            describedBy={billingRead.state === 'none' ? undefined : ['acct-billing-email-state']}
            onInput={(v: string) => setEmailBox(v)}
          />
          {billingRead.state !== 'none' && (
            <p class="field-hint" id="acct-billing-email-state">
              {billingRead.state === 'read'
                ? billingRead.target === 'billing-party'
                  ? 'This address is kept on the payer above, so it is the one used for every account '
                    + 'billed to them. Changing it here changes it for all of them.'
                  : 'This address is kept on this account.'
                : billingRead.state === 'loading'
                  ? 'Reading the billing email for this account.'
                  : billingRead.message}
            </p>
          )}

          {/* ⭐ Board 2764 M1 (R-A2) - THE BILLING PARTY'S POSTAL ADDRESS, edit-only and
              only when the account bills to a PARTY. It is the address Stripe prints
              under "Bill to", so it belongs to the payer above, not to this account;
              an account that pays for itself is addressed with its own fields above.
              A `fieldset` for its `legend` (the `.delivery-group` precedent in
              SendQuote.tsx), so the four boxes have one visible, announced name.
              ⛔ DISABLED UNTIL THE READ ANSWERS, for the billing email's reason. */}
          {billingRead.state === 'read' && billingRead.target === 'billing-party' && (
            <fieldset class="delivery-group">
              <legend class="delivery-group-label">Billing address</legend>
              <FormField
                id="acct-billing-address-line"
                label="Billing address line"
                value={addressBox.addressLine}
                opaque
                autoComplete="off"
                disabled={addressRead.state !== 'read'}
                describedBy={['acct-billing-address-state']}
                onInput={(v: string) => setAddressBox((prev) => ({ ...prev, addressLine: v }))}
              />
              <FormField
                id="acct-billing-town"
                label="Billing town"
                value={addressBox.town}
                opaque
                autoComplete="off"
                disabled={addressRead.state !== 'read'}
                describedBy={['acct-billing-address-state']}
                onInput={(v: string) => setAddressBox((prev) => ({ ...prev, town: v }))}
              />
              <FormField
                id="acct-billing-county"
                label="Billing county"
                value={addressBox.county}
                opaque
                autoComplete="off"
                disabled={addressRead.state !== 'read'}
                describedBy={['acct-billing-address-state']}
                onInput={(v: string) => setAddressBox((prev) => ({ ...prev, county: v }))}
              />
              <FormField
                id="acct-billing-eircode"
                label="Billing Eircode"
                value={addressBox.eircode}
                opaque
                autoComplete="off"
                autocapitalize="characters"
                spellcheck={false}
                disabled={addressRead.state !== 'read'}
                describedBy={['acct-billing-address-state']}
                onInput={(v: string) => setAddressBox((prev) => ({ ...prev, eircode: v }))}
              />
              <p class="field-hint" id="acct-billing-address-state">
                {addressRead.state === 'read'
                  ? 'This address is kept on the payer above, so it goes on the invoices for every account '
                    + 'billed to them. Changing it here changes it for all of them.'
                  : addressRead.state === 'failed'
                    ? addressRead.message
                    : 'Reading the billing address for this account.'}
              </p>
            </fieldset>
          )}

          {/* ⭐ Board 2764 M2 (R-A3, R-11) - BANK DETAILS, edit-only and only for a
              PARTY payer, beside the email and address they are set up from. The
              press creates or reuses the payer's record at Stripe and shows the
              account it pays into; nothing is invoiced. ⛔ No home or payer name is
              in this fixed copy: the payer is named by the picker above. */}
          {billingRead.state === 'read' && billingRead.target === 'billing-party' && (
            <fieldset class="delivery-group">
              <legend class="delivery-group-label">Bank details</legend>
              {bankDetails === null ? (
                /* ⭐ board 2764 M7 (M2 ux-review Mediums, folded into the kiosk-screen
                   pass): the hint is TRUE about the form. Why sits BELOW this button,
                   so it says "below"; and a payer with no SAVED billing email says so
                   BEFORE the press, which the server would refuse (`no_billing_email`),
                   and the button is disabled with this sentence as its description.
                   Saving returns the form to Add, so the next Edit re-reads the email. */
                <p class="field-hint" id="acct-bank-details-hint">
                  {(billingRead.billingEmail ?? '').trim() === ''
                    ? 'Add a billing email and save the account first.'
                    : 'Sets up this payer at Stripe, if that has not been done yet, and shows the bank account '
                      + 'they pay into. Nothing is invoiced. Give a reason in the Why box below first.'}
                </p>
              ) : (
                <>
                  <FactsTable
                    facts={[
                      { term: 'Account holder', value: bankDetails.accountHolderName ?? 'Not given by Stripe' },
                      { term: 'IBAN', value: ibanForReading(bankDetails.iban) },
                      { term: 'BIC', value: bankDetails.bic ?? 'Not given by Stripe' },
                    ]}
                  />
                  <p class="field-hint" id="acct-bank-details-hint">
                    This is the account this payer pays into. It never changes.
                  </p>
                </>
              )}
              <Button
                variant="quiet"
                disabled={busy || (billingRead.billingEmail ?? '').trim() === ''}
                ariaDescribedBy="acct-bank-details-hint"
                onClick={onSetUpBankDetails}
              >
                Set up bank details
              </Button>
            </fieldset>
          )}

          {/* ⭐⭐ board 3059 - RESIDENT NAMES ON INVOICES, edit-only, for EITHER payer
              (an account that bills itself, or the party above). OFF unless Helen
              turns it on here; nothing else turns it on. One quiet control that
              swaps itself for a confirm plus a Cancel (the screen's existing
              reveal shape), and the reason is the form's Why below, as Bank
              details uses it. ⛔ No home, payer or resident name in this fixed
              copy beyond the billing email the confirm must name. */}
          {billingRead.state === 'read' && (
            <fieldset class="delivery-group">
              <legend class="delivery-group-label">Resident names on invoices</legend>
              <p class="field-hint" id="acct-resident-names-state">
                {residentNamesStateWords(billingRead.residentNames, billingRead.target)}
              </p>
              {namesConfirm === null ? (
                <Button
                  variant="quiet"
                  disabled={busy}
                  ariaDescribedBy="acct-resident-names-state"
                  onClick={() => setNamesConfirm(!billingRead.residentNames)}
                >
                  {billingRead.residentNames ? 'Turn resident names off' : 'Turn resident names on'}
                </Button>
              ) : (
                <>
                  <p class="field-hint" id="acct-resident-names-confirm">
                    {residentNamesConfirmWords(namesConfirm, billingRead.billingEmail)}
                  </p>
                  <div class="admin-add-actions">
                    <Button
                      variant="secondary"
                      disabled={busy}
                      ariaDescribedBy="acct-resident-names-confirm"
                      onClick={onConfirmResidentNames}
                    >
                      {namesConfirm ? 'Turn on' : 'Turn off'}
                    </Button>
                    <Button variant="quiet" disabled={busy} onClick={() => setNamesConfirm(null)}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </fieldset>
          )}

          {/* ⛔ THE REASON, AND IT SITS OUTSIDE THE SAVE BUTTON'S SCOPE ON
              PURPOSE: the two row buttons use it as well, so taking an account out
              of use also has to be explained. One field, one rule, four actions. */}
          {/* ⭐⭐ T1749 M7 rework 1 — `FormField`, hint and error as its own props.
              The seen order (hint then error), the read order (error first in
              `aria-describedby`) and the derived ids are the component's now; the
              arms that graded all three are re-pointed to `acct-note-hint` /
              `acct-note-error`, which is what `${id}-hint` / `${id}-error` produce.
              ⚠️ THE BANNER KEEPS THE SENTENCE AND ITS `role="alert"`: this is
              redundancy at the field, not a move, and both read from ONE constant
              (`REASON_REFUSAL`) so they cannot drift into two sentences. */}
          <FormField
            id="acct-note"
            label="Why"
            value={note}
            opaque
            autoComplete="off"
            elementRef={noteRef}
            hint={namesConfirm === true
              ? NAMES_ON_WHY_HINT
              : 'A few words. Needed when you add, edit or take an account out of use.'}
            error={refusedField === 'form' ? REASON_REFUSAL : undefined}
            onInput={(v: string) => { setRefusedField(null); setNote(v); }}
          />

          <div class="admin-add-actions">
            <button
              type="button"
              class="btn btn--primary"
              data-fb-opaque
              disabled={busy}
              onClick={onSave}
            >
              {editing == null ? 'Add account' : 'Save this account'}
            </button>
            {editing != null && (
              <button
                type="button"
                class="btn btn--quiet"
                data-fb-opaque
                disabled={busy}
                onClick={startAdd}
              >
                Stop editing
              </button>
            )}
          </div>
        </div>
        </>
      )}
    </>
  );
}
